import { observable, computed, action } from 'mobx';
import { TBlockType } from '~/types/model.types';
import { IStyles } from '~/types/block-styles.types';
import { TBlockResponse } from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { TBlock } from '~/types/builder.types';
import type { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import { CXLeadDetailsBlock } from '~/components/ClientExperience/OneFlow/models/cx-lead-details-block.model';

export class CXBaseBlock {
    @observable block_id: string;
    @observable connected_variable_id: string;
    @observable flowRevision: FlowRevision<CXRootStore>;
    @observable styles: IStyles = {};
    flow_id: string;
    page_id: string;
    parent_id: string;
    type: TBlockType;

    rootStore: CXRootStore;

    constructor(
        block: TBlockResponse | TBlock,
        flowRevison: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        Object.assign(this, block);
        this.styles = block.styles || {};

        this.rootStore = rootStore;
        this.flowRevision = flowRevison;
    }

    get displayName() {
        return this.type.toString();
    }

    @action
    mergeBlockAttributes(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        block: TBlockResponse,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        attributesToMerge: Record<string, any>
    ) {}

    @action
    setFlowRevision(flowRevision: FlowRevision<CXRootStore>) {
        this.flowRevision = flowRevision;
    }

    get errors() {
        return {};
    }

    get blockInputValidations() {
        return {};
    }

    get parent() {
        return <CXContainerableBlock>(
            this.flowRevision.blocksMap[this.parent_id]
        );
    }

    @computed
    get isActionBlockForPrint() {
        return false;
    }

    @computed
    get isRootChild() {
        return this.parent && this.parent.isRootBlock;
    }

    @computed
    get isPendingApproval() {
        return this.flow.changes_pending_approval?.find(
            change => change.block_id === this.block_id && !change.accepted
        );
    }

    @computed
    get isPendingClientCompletion() {
        return false;
    }

    @computed
    get page() {
        return this.flowRevision.pagesMap[this.page_id];
    }

    get isCompanyInfoBlock() {
        return this.type === 'company_info_block';
    }

    get isPaymentScheduleBlock() {
        return this.type === 'payment_schedule_block';
    }

    get isPaymentBlock() {
        return this.type === 'payment_block';
    }

    get isServicesBlock() {
        return this.type === 'services_block';
    }

    get isContractHtmlBlock() {
        return this.type === 'contract_html_block';
    }

    get isServiceSelectionBlock() {
        return this.type === 'service_selection_block';
    }

    get isChargeContainerBlock() {
        return this.type === 'charge_container_block';
    }

    get isContractBlock() {
        return this.type === 'contract_block';
    }

    get isInvoiceContainerBlock() {
        return this.type === 'invoice_container_block';
    }

    get isScheduleContainerBlock() {
        return this.type === 'schedule_container_block';
    }

    get isPayContainerBlock() {
        return this.type === 'charge_container_block';
    }

    get isQuestionBlock() {
        return this.type === 'question_block';
    }

    isLeadDetailsBlock(): this is CXLeadDetailsBlock {
        return this.type === 'lead_details_block';
    }

    // Gets the first parent that is a root level block
    calculateRootLevelParent() {
        if (this.isRootChild) {
            return null;
        }

        let { parent } = this;

        while (parent && !parent.isRootChild) {
            parent = parent.parent;
        }
        return parent;
    }

    @computed
    get connectedVariable() {
        return this.flowRevision.variablesMap[this.connected_variable_id];
    }

    @computed
    get flow() {
        return this.rootStore.flowStore.flow;
    }

    @computed
    get index() {
        return this.parent?.childBlocks.findIndex(
            block => block.block_id === this.block_id
        );
    }

    @computed
    get displayOrder() {
        return this.index + 1;
    }
}
